import React from 'react';

export interface SharedPricingCardsProps {
    plans: any[];
    onPlanAction: (planId: string) => void;
    getButtonLabel: (plan: any) => string;
    isPaymentLoading?: boolean;
    layout?: 'desktop' | 'mobile'; // To handle specific responsive margins if needed
}

const SharedPricingCards: React.FC<SharedPricingCardsProps> = ({
    plans, onPlanAction, getButtonLabel, isPaymentLoading = false, layout = 'desktop'
}) => {
    // --- Inline SVG Icons ---
    const CheckIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width={layout === 'mobile' ? '16' : '18'} height={layout === 'mobile' ? '16' : '18'} viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
    );

    const XIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width={layout === 'mobile' ? '16' : '18'} height={layout === 'mobile' ? '16' : '18'} viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
    );

    const isMobile = layout === 'mobile';

    return (
        <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: isMobile ? '15px' : '30px', 
            width: '100%' 
        }}>
            {plans.map((plan: any, index: number) => (
                <div key={index} style={{
                    background: 'var(--bg-box, #ffffff)', 
                    borderRadius: isMobile ? '12px' : '16px', 
                    padding: isMobile ? '20px 16px' : '32px',
                    boxShadow: (plan.popular || plan.badgeText) ? '0 10px 40px rgba(59, 130, 246, 0.12)' : '0 4px 20px rgba(0,0,0,0.05)',
                    border: plan.popular ? '2px solid #3b82f6' : (plan.badgeText ? '2px solid #8b5cf6' : '1px solid var(--border-color, #E8E4F0)'),
                    position: 'relative', 
                    transform: (!isMobile && plan.popular) ? 'scale(1.03)' : 'scale(1)', 
                    transition: 'all 0.3s ease',
                    display: 'flex', 
                    flexDirection: 'column'
                }}>
                    {/* Badges */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: (plan.popular || plan.current || plan.badgeText) ? '12px' : '0', position: isMobile ? 'static' : 'absolute', top: isMobile ? 'auto' : '-12px', right: isMobile ? 'auto' : '16px', left: isMobile ? 'auto' : (plan.current ? '16px' : 'auto') }}>
                        {plan.popular && (
                            <span style={{
                                background: 'linear-gradient(135deg, #f59e0b 0%, #dc2626 100%)', color: 'white',
                                padding: '4px 12px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '700',
                            }}>
                                ⭐ RECOMMANDÉ
                            </span>
                        )}
                        {plan.badgeText && (
                            <span style={{
                                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)', color: 'white',
                                padding: '4px 12px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '700',
                            }}>
                                💎 {plan.badgeText}
                            </span>
                        )}
                        {plan.current && (
                            <span style={{
                                background: '#10b981', color: 'white',
                                padding: '4px 12px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '700',
                            }}>
                                ✓ Plan actuel
                            </span>
                        )}
                    </div>

                    {/* Plan Name & Price */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                        <h3 style={{ fontSize: isMobile ? '1.2rem' : '1.3rem', fontWeight: '800', color: plan.color, margin: 0 }}>{plan.name}</h3>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end' }}>
                                <span style={{ fontSize: isMobile ? '1.4rem' : '1.8rem', fontWeight: '900', color: 'var(--text-heading, #1E1B2E)' }}>{plan.price}</span>
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted, #9CA3AF)', marginLeft: '4px' }}>{plan.period}</span>
                            </div>
                            {plan.billingText && (
                                <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '2px 0 0 0', fontWeight: '500' }}>
                                    {plan.billingText}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Features list */}
                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px 0', flexGrow: 1 }}>
                        {plan.features.map((feature: any, i: number) => (
                            <li key={i} style={{
                                padding: '8px 0', display: 'flex', alignItems: 'flex-start', gap: '10px',
                                borderBottom: i < plan.features.length - 1 ? '1px solid var(--border-color, #f1f5f9)' : 'none',
                                opacity: feature.included ? 1 : 0.5,
                            }}>
                                <div style={{ marginTop: '2px', flexShrink: 0 }}>
                                    {feature.included ? <CheckIcon /> : <XIcon />}
                                </div>
                                <span style={{ 
                                    fontSize: isMobile ? '0.8rem' : '0.85rem', 
                                    color: feature.included ? 'var(--text-main, #4B5563)' : '#94a3b8',
                                    lineHeight: '1.4',
                                    fontWeight: feature.included ? '500' : '400'
                                }}>
                                    {feature.text}
                                </span>
                            </li>
                        ))}
                    </ul>

                    {/* CTA Button */}
                    <div style={{ marginTop: 'auto' }}>
                        <button
                            onClick={() => onPlanAction(plan.planId)}
                            disabled={plan.current || (plan.planId === 'gratuit' && getButtonLabel(plan) === 'Plan actuel') || isPaymentLoading}
                            style={{
                                width: '100%', padding: '14px', borderRadius: '10px', fontSize: '1rem', fontWeight: '800',
                                border: plan.current ? '1px solid var(--border-color, #E8E4F0)' : 'none', 
                                cursor: plan.current || (plan.planId === 'gratuit' && getButtonLabel(plan) === 'Plan actuel') || isPaymentLoading ? 'not-allowed' : 'pointer', 
                                minHeight: '48px',
                                background: plan.current || (plan.planId === 'gratuit' && getButtonLabel(plan) === 'Plan actuel') ? 'var(--bg-input, #F8F7FF)' : `linear-gradient(135deg, ${plan.color} 0%, ${plan.color}e6 100%)`,
                                color: plan.current || (plan.planId === 'gratuit' && getButtonLabel(plan) === 'Plan actuel') ? 'var(--text-muted, #9CA3AF)' : 'white',
                                boxShadow: plan.current || (plan.planId === 'gratuit' && getButtonLabel(plan) === 'Plan actuel') ? 'none' : `0 4px 15px rgba(0,0,0,0.15)`,
                                transition: 'all 0.2s',
                                opacity: isPaymentLoading && plan.planId !== 'gratuit' && !plan.current ? 0.7 : 1,
                                textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center'
                            }}
                        >
                            {getButtonLabel(plan)}
                        </button>
                    </div>
                </div>
            ))}

            {/* B2B / Institutionnel & Formateurs Banner */}
            <div style={{
                gridColumn: '1 / -1',
                marginTop: isMobile ? '10px' : '20px',
                padding: isMobile ? '16px' : '24px 28px',
                background: 'linear-gradient(135deg, #1E1B2E 0%, #2D274A 100%)',
                borderRadius: isMobile ? '12px' : '16px',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '16px',
                boxShadow: '0 8px 30px rgba(30, 27, 46, 0.25)'
            }}>
                <div style={{ maxWidth: '680px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.1)', padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700', color: '#c084fc', marginBottom: '8px' }}>
                        🏢 ENTREPRISES · SGI · INSTITUTIONNELS · FORMATEURS
                    </div>
                    <h4 style={{ margin: 0, fontSize: isMobile ? '1rem' : '1.15rem', fontWeight: 800, color: '#ffffff' }}>
                        Vous êtes une SGI, une banque, une société de gestion ou un coach / académies en Bourse ?
                    </h4>
                    <p style={{ margin: '6px 0 0 0', fontSize: isMobile ? '0.8rem' : '0.88rem', color: '#cbd5e1', lineHeight: '1.5' }}>
                        Les abonnements ci-dessus sont <strong>strictement réservés aux investisseurs particuliers à titre personnel (B2C)</strong>. 
                        Pour une licence d'entreprise, un accès multi-utilisateurs, une intégration API ou un usage de formation/coaching, contactez-nous pour une offre B2B sur-mesure.
                    </p>
                </div>
                <a 
                    href="mailto:contact@kaneabourse.com?subject=Demande%20Offre%20B2B%20/%20Institutionnel%20/%20Formateur"
                    style={{
                        padding: isMobile ? '12px 20px' : '14px 24px',
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #6C3FC4 100%)',
                        color: 'white',
                        borderRadius: '10px',
                        fontSize: '0.9rem',
                        fontWeight: '800',
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)',
                        transition: 'transform 0.2s',
                        textAlign: 'center',
                        width: isMobile ? '100%' : 'auto'
                    }}
                >
                    Demander une offre B2B →
                </a>
            </div>
        </div>
    );
};

export default SharedPricingCards;
